import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getFieldsByCategory, FieldDefinition, createField } from '../../../lib/fieldRegistry';
import { useBuilderStore } from '../../../stores/builderStore';
import type { FieldType } from '../../../types/form';

interface MobileFieldPaletteProps {
  onClose: () => void;
  onFieldAdded?: () => void;
}

type Category = 'basic' | 'advanced' | 'layout';

const categoryLabels: Record<Category, string> = {
  basic: 'Basic',
  advanced: 'Advanced',
  layout: 'Layout',
};

const categoryIcons: Record<Category, string> = {
  basic: '📝',
  advanced: '⚡',
  layout: '📐',
};

function FieldButton({
  field,
  onAdd
}: {
  field: FieldDefinition;
  onAdd: (type: FieldType) => void;
}) {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => onAdd(field.type)}
        onTouchStart={() => field.description && setShowTooltip(true)}
        onTouchEnd={() => setShowTooltip(false)}
        className="w-full flex flex-col items-center justify-center p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl active:scale-95 active:bg-gray-50 dark:active:bg-gray-700 transition-all touch-manipulation"
      >
        <span className="text-2xl mb-2">{field.icon}</span>
        <span className="text-xs font-medium text-gray-700 dark:text-gray-200 text-center leading-tight">
          {field.label}
        </span>
        {field.description && (
          <span className="mt-1">
            <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </span>
        )}
      </button>

      {/* Tooltip */}
      {field.description && showTooltip && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl animate-fadeIn">
          <div className="font-semibold mb-1">{field.label}</div>
          <div className="text-gray-300">{field.description}</div>
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1">
            <div className="w-2 h-2 bg-gray-900 transform rotate-45"></div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function MobileFieldPalette({ onClose, onFieldAdded }: MobileFieldPaletteProps) {
  const { t } = useTranslation('mobile');
  const [activeCategory, setActiveCategory] = useState<Category>('basic');
  const [searchTerm, setSearchTerm] = useState('');
  const { addField } = useBuilderStore();

  // Memoize field arrays since getFieldsByCategory returns from a static registry
  const basicFields = useMemo(() => getFieldsByCategory('basic'), []);
  const advancedFields = useMemo(() => getFieldsByCategory('advanced'), []);
  const layoutFields = useMemo(() => getFieldsByCategory('layout'), []);

  const allFields = useMemo<Record<Category, FieldDefinition[]>>(() => ({
    basic: basicFields,
    advanced: advancedFields,
    layout: layoutFields,
  }), [basicFields, advancedFields, layoutFields]);

  const filterFields = (fields: FieldDefinition[]) => {
    if (!searchTerm.trim()) return fields;
    const term = searchTerm.toLowerCase();
    return fields.filter(
      (field) =>
        field.label.toLowerCase().includes(term) ||
        field.type.toLowerCase().includes(term)
    );
  };

  const handleAddField = useCallback((type: FieldType) => {
    const newField = createField(type);
    // Use current selectedFieldId from store directly for positioning
    const { selectedFieldId: currentSelectedId } = useBuilderStore.getState();
    addField(newField, currentSelectedId || undefined);
    onFieldAdded?.();
    onClose();
  }, [addField, onFieldAdded, onClose]);

  const filteredFields = filterFields(allFields[activeCategory]);

  // Get all filtered fields for search mode
  const allFilteredFields = searchTerm.trim()
    ? [...filterFields(basicFields), ...filterFields(advancedFields), ...filterFields(layoutFields)]
    : [];

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 safe-area-inset-top">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('fieldPalette.title')}</h2>
          <button
            onClick={onClose}
            aria-label={t('common.close')}
            className="p-2 -mr-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 touch-manipulation"
          >
            <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder={t('fieldPalette.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-10 py-3 text-base border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              aria-label={t('fieldPalette.clearSearch')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 touch-manipulation"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Category Tabs - Hidden when searching */}
      {!searchTerm.trim() && (
        <div className="flex gap-2 px-4 py-3 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 overflow-x-auto scrollbar-hide">
          {(Object.keys(categoryLabels) as Category[]).map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-colors touch-manipulation ${
                activeCategory === category
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
            >
              <span>{categoryIcons[category]}</span>
              <span className="font-medium">{t(`fieldPalette.categories.${category}`)}</span>
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                activeCategory === category
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
              }`}>
                {allFields[category].length}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Field Grid */}
      <div className="flex-1 overflow-y-auto p-4 pb-safe">
        {searchTerm.trim() ? (
          // Search results
          allFilteredFields.length > 0 ? (
            <div className="grid grid-cols-3 gap-3">
              {allFilteredFields.map((field) => (
                <FieldButton key={field.type} field={field} onAdd={handleAddField} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-48 text-gray-500 dark:text-gray-400">
              <svg className="w-12 h-12 mb-3 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <p>{t('fieldPalette.noResults', { term: searchTerm })}</p>
            </div>
          )
        ) : (
          // Category view
          filteredFields.length > 0 ? (
            <div className="grid grid-cols-3 gap-3">
              {filteredFields.map((field) => (
                <FieldButton key={field.type} field={field} onAdd={handleAddField} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-48 text-gray-500 dark:text-gray-400">
              <p>{t('fieldPalette.emptyCategory')}</p>
            </div>
          )
        )}
      </div>

      {/* Quick Add Hint */}
      <div className="px-4 py-3 bg-gray-100 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 safe-area-inset-bottom">
        <p className="text-xs text-center text-gray-500 dark:text-gray-400">
          {t('fieldPalette.tapToAdd')}
        </p>
      </div>
    </div>
  );
}
